import { PUBLIC_LEADGEN_HOST } from '$env/static/public';

const DEFAULT_PREFIX = '/public-api';

interface FetchOptions {
	method?: string;
	prefix?: string;
	searchParams?: Record<string, unknown>;
	json?: unknown;
	formData?: FormData;
	headers?: Record<string, string>;
	signal?: AbortSignal;
}

interface SSECallbacks<T> {
	onField?: (key: string, value: unknown) => void;
	onFieldReset?: () => void;
	onProgress?: (event: { stage: string; completed?: number; total?: number }) => void;
	onResult?: (data: T) => void;
	onError?: (error: Error) => void;
}

function replaceNulls(value: unknown): unknown {
	if (value === null) return 'null';
	if (Array.isArray(value)) return value.map(replaceNulls);
	if (typeof value === 'object') {
		const result: Record<string, unknown> = {};
		for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
			if (v === undefined) continue;
			result[k] = replaceNulls(v);
		}
		return result;
	}
	return value;
}

function encodeParams(params: Record<string, unknown>): Record<string, string> {
	const result: Record<string, string> = {};

	for (const [key, value] of Object.entries(params)) {
		if (value === undefined) continue;

		if (value === null) {
			result[key] = 'null';
			continue;
		}

		if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
			result[key] = JSON.stringify(replaceNulls(value));
			continue;
		}

		result[key] = String(value);
	}

	return result;
}

export async function fetchApi<T = unknown>(
	fetch_fn_or_options: typeof fetch | (FetchOptions & { url: string }),
	options_or_undefined?: FetchOptions & { url: string }
): Promise<T> {
	let fetch_to_use: typeof fetch;
	let options: FetchOptions & { url: string };

	if (typeof fetch_fn_or_options === 'function') {
		fetch_to_use = fetch_fn_or_options;
		options = options_or_undefined!;
	} else {
		fetch_to_use = fetch;
		options = fetch_fn_or_options;
	}

	const {
		url,
		prefix = DEFAULT_PREFIX,
		searchParams,
		json,
		formData,
		method = 'GET',
		headers = {},
		signal,
	} = options;

	let full_url = `${PUBLIC_LEADGEN_HOST}${prefix}${url}`;

	if (searchParams) {
		const encoded = encodeParams(searchParams);
		const query = new URLSearchParams(encoded).toString();
		if (query) {
			full_url += `?${query}`;
		}
	}

	const fetch_options: RequestInit = {
		method,
		signal
	};

	if (formData) {
		fetch_options.body = formData;
		fetch_options.headers = { ...headers };
	} else {
		fetch_options.headers = {
			'Content-Type': 'application/json',
			...headers
		};
		if (json !== undefined && method !== 'GET') {
			fetch_options.body = JSON.stringify(json);
		}
	}

	try {
		const response = await fetch_to_use(full_url, fetch_options);
		const data = await response.json();

		if (!response.ok) {
			throw new Error(data.error || data.message || 'Request failed');
		}

		return data as T;
	} catch (error: unknown) {
		const message = error instanceof Error ? error.message : 'Network error';
		throw new Error(message, { cause: error });
	}
}

async function tryParseJSON(response: Response): Promise<unknown> {
	const text = await response.text();
	if (!text.trim()) return null;
	try {
		return JSON.parse(text);
	} catch {
		throw new Error(`Unexpected response: ${text.slice(0, 200)}`);
	}
}

export async function fetchSSE<T = unknown>(
	options: FetchOptions & { url: string },
	callbacks: SSECallbacks<T>
): Promise<T> {
	const {
		url,
		prefix = DEFAULT_PREFIX,
		formData,
		json,
		method = 'POST',
		headers = {},
		signal,
	} = options;

	const full_url = `${PUBLIC_LEADGEN_HOST}${prefix}${url}`;

	const fetch_options: RequestInit = {
		method,
		signal
	};

	if (formData) {
		fetch_options.body = formData;
		fetch_options.headers = { ...headers, Accept: 'text/event-stream' };
	} else {
		fetch_options.headers = {
			'Content-Type': 'application/json',
			Accept: 'text/event-stream',
			...headers
		};
		if (json !== undefined) {
			fetch_options.body = JSON.stringify(json);
		}
	}

	const response = await fetch(full_url, fetch_options);

	if (!response.ok) {
		let error_message = 'Request failed';
		try {
			const data = await tryParseJSON(response);
			if (data && typeof data === 'object') {
				const obj = data as Record<string, unknown>;
				error_message = (obj.error || obj.message || error_message) as string;
			}
		} catch (e) {
			console.error('error :>> ', e);
		}
		const err = new Error(error_message);
		callbacks.onError?.(err);
		throw err;
	}

	const content_type = response.headers.get('content-type') || '';

	if (!content_type.includes('text/event-stream')) {
		const data = (await tryParseJSON(response)) as T;
		if (data === null) {
			const err = new Error('Empty response body');
			callbacks.onError?.(err);
			throw err;
		}
		callbacks.onResult?.(data);
		return data;
	}

	const reader = response.body!.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	let result_data: T | undefined;

	try {
		for (;;) {
			const { done, value } = await reader.read();
			if (done) break;

			buffer += decoder.decode(value, { stream: true });

			const boundary = buffer.lastIndexOf('\n\n');
			if (boundary === -1) continue;

			const complete = buffer.slice(0, boundary + 2);
			buffer = buffer.slice(boundary + 2);

			const blocks = complete.split('\n\n').filter(Boolean);

			for (const block of blocks) {
				const data_line = block.split('\n').find((line) => line.startsWith('data: '));
				if (!data_line) continue;

				const event_str = data_line.slice(6);
				let event: { type: string; [key: string]: unknown };
				try {
					event = JSON.parse(event_str);
				} catch {
					continue;
				}

				if (event.type === 'progress') {
					callbacks.onProgress?.({
						stage: (event.stage as string) || '',
						completed: event.completed as number | undefined,
						total: event.total as number | undefined
					});
				} else if (event.type === 'field') {
					callbacks.onField?.(event.key as string, event.value);
				} else if (event.type === 'field_reset') {
					callbacks.onFieldReset?.();
				} else if (event.type === 'result') {
					result_data = event.data as T;
					callbacks.onResult?.(result_data);
				} else if (event.type === 'error') {
					const err = new Error((event.message as string) || 'Stream error');
					callbacks.onError?.(err);
					throw err;
				}
			}
		}

		if (buffer.trim()) {
			const remaining_blocks = buffer.split('\n\n').filter(Boolean);
			for (const block of remaining_blocks) {
				const data_line = block.split('\n').find((line) => line.startsWith('data: '));
				if (!data_line) continue;

				const event_str = data_line.slice(6);
				let event: { type: string; [key: string]: unknown };
				try {
					event = JSON.parse(event_str);
				} catch {
					continue;
				}

				if (event.type === 'result') {
					result_data = event.data as T;
					callbacks.onResult?.(result_data);
				} else if (event.type === 'error') {
					const err = new Error((event.message as string) || 'Stream error');
					callbacks.onError?.(err);
					throw err;
				}
			}
		}
	} finally {
		reader.releaseLock();
	}

	if (result_data === undefined) {
		const err = new Error('Stream ended without result');
		callbacks.onError?.(err);
		throw err;
	}

	return result_data;
}